import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Button, 
  Card, 
  Grid, 
  Header, 
  Label, 
  Segment, 
  Table, 
  Message,
  Loader,
  Statistic,
  Icon,
  Tab,
  Progress,
  Container,
  Divider,
  Popup
} from 'semantic-ui-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  AreaChart,
  Area,
  Legend
} from 'recharts';
import { API, showError, isAdmin } from '../../helpers';
import { renderQuota, renderNumber } from '../../helpers/render';
import { StatusContext } from '../../context/Status';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d'];

const UserInfo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [statusState] = useContext(StatusContext);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState(null);
  const [enhancedSummary, setEnhancedSummary] = useState(null);
  const [activityTrend, setActivityTrend] = useState([]);
  const [hourlyDistribution, setHourlyDistribution] = useState([]);
  const [modelUsage, setModelUsage] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [apiStats, setApiStats] = useState([]);
  const [dataStats, setDataStats] = useState(null);
  
  // 判断 Git 功能是否启用
  const gitEnabled = statusState?.status?.git_enabled !== false;

  const loadUserInfo = async () => {
    setLoading(true);
    try {
      const res = await API.get(`/api/user/${id}`);
      const { success, message, data } = res.data;
      if (success) {
        setUser(data);
      } else {
        showError(message);
        navigate('/user');
      }
    } catch (error) {
      showError('加载用户信息失败');
      navigate('/user');
    }
    setLoading(false);
  };

  const loadUserStats = async () => {
    try {
      // 加载基础统计
      const statsRes = await API.get(`/api/user/${id}/stats`);
      if (statsRes.data.success) {
        setUserStats(statsRes.data.data);
      }

      // 如果是管理员，加载详细行为数据
      if (isAdmin()) {
        try {
          // 加载增强版行为摘要
          const enhancedRes = await API.get(`/api/admin/user/${id}/behavior/enhanced`);
          if (enhancedRes.data.success) {
            setEnhancedSummary(enhancedRes.data.data);
          }

          // 加载活动趋势
          const trendRes = await API.get(`/api/admin/user/${id}/behavior/trend?days=30`);
          if (trendRes.data.success) {
            setActivityTrend(trendRes.data.data);
          }

          // 加载小时分布
          const hourlyRes = await API.get(`/api/admin/user/${id}/behavior/hourly`);
          if (hourlyRes.data.success) {
            const data = Object.entries(hourlyRes.data.data || {}).map(([hour, count]) => ({
              hour: `${hour}:00`,
              requests: count
            }));
            setHourlyDistribution(data);
          }

          // 加载模型使用
          const modelRes = await API.get(`/api/admin/user/${id}/behavior/models`);
          if (modelRes.data.success) {
            const data = Object.entries(modelRes.data.data || {}).map(([model, count]) => ({
              name: model,
              value: count
            }));
            setModelUsage(data);
          }

          // 加载最近日志
          const logsRes = await API.get(`/api/admin/user/${id}/behavior/logs?page=1&page_size=10`);
          if (logsRes.data.success) {
            setRecentLogs(logsRes.data.data.logs || []);
          }

          // 加载API统计
          const apiStatsRes = await API.get(`/api/admin/user/${id}/api-stats?days=30`);
          if (apiStatsRes.data.success) {
            setApiStats(apiStatsRes.data.data.endpoints || []);
          }

          // 加载数据统计（仓库、执行、设备）
          const dataStatsRes = await API.get(`/api/user/${id}/data-stats`);
          if (dataStatsRes.data.success) {
            setDataStats(dataStatsRes.data.data);
          }
        } catch (error) {
          console.log('加载行为数据失败:', error.message);
        }
      }
    } catch (error) {
      console.error('加载用户统计信息失败:', error);
    }
  };

  useEffect(() => {
    if (id) {
      loadUserInfo();
      loadUserStats();
    }
  }, [id]);

  if (loading) {
    return (
      <Segment>
        <Loader active inline='centered'>加载中...</Loader>
      </Segment>
    );
  }

  if (!user) {
    return (
      <Segment>
        <Message error>用户不存在</Message>
      </Segment>
    );
  }

  // 获取活跃度等级显示
  const getActivityLevelInfo = (level) => {
    const levels = {
      'power_user': { label: '高频用户', color: 'purple', icon: 'fire' },
      'active': { label: '活跃用户', color: 'green', icon: 'check circle' },
      'casual': { label: '普通用户', color: 'blue', icon: 'user' },
      'dormant': { label: '沉睡用户', color: 'orange', icon: 'moon' },
      'new': { label: '新用户', color: 'teal', icon: 'star' }
    };
    return levels[level] || { label: '未知', color: 'grey', icon: 'question' };
  };

  const panes = [
    {
      menuItem: { key: 'overview', icon: 'dashboard', content: '概览' },
      render: () => (
        <Tab.Pane>
          <OverviewTab 
            user={user} 
            enhancedSummary={enhancedSummary}
            activityTrend={activityTrend}
          />
        </Tab.Pane>
      )
    },
    {
      menuItem: { key: 'basic', icon: 'info', content: '基本信息' },
      render: () => (
        <Tab.Pane>
          <BasicInfoTab user={user} />
        </Tab.Pane>
      )
    },
    {
      menuItem: { key: 'quota', icon: 'pie chart', content: '配额管理' },
      render: () => (
        <Tab.Pane>
          <QuotaManagementTab user={user} userStats={userStats} />
        </Tab.Pane>
      )
    }
  ];

  // 如果是管理员，添加更多标签页
  if (isAdmin() && enhancedSummary) {
    panes.push({
      menuItem: { key: 'behavior', icon: 'chart line', content: '行为分析' },
      render: () => (
        <Tab.Pane>
          <BehaviorAnalysisTab 
            summary={enhancedSummary}
            trend={activityTrend}
            hourly={hourlyDistribution}
            models={modelUsage}
            logs={recentLogs}
          />
        </Tab.Pane>
      )
    });

    panes.push({
      menuItem: { key: 'api', icon: 'code', content: 'API使用' },
      render: () => (
        <Tab.Pane>
          <APIUsageTab apiStats={apiStats} />
        </Tab.Pane>
      )
    });

    // 数据统计 Tab（仓库、执行、设备）
    if (dataStats) {
      panes.push({
        menuItem: { key: 'data', icon: 'database', content: '数据统计' },
        render: () => (
          <Tab.Pane>
            <DataStatsTab dataStats={dataStats} navigate={navigate} userId={id} />
          </Tab.Pane>
        )
      });
    }
  }

  const activityInfo = enhancedSummary ? getActivityLevelInfo(enhancedSummary.activity_level) : null;

  return (
    <Container fluid style={{ padding: '20px' }}>
      {/* 用户头部信息卡片 */}
      <Segment style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        borderRadius: '12px',
        marginBottom: '20px'
      }}>
        <Grid stackable>
          <Grid.Row>
            <Grid.Column width={2} textAlign='center' verticalAlign='middle'>
              <div style={{ 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto'
              }}>
                <Icon name='user' size='huge' style={{ color: 'white', margin: 0 }} />
              </div>
            </Grid.Column>
            <Grid.Column width={10}>
              <Header as='h1' inverted style={{ marginBottom: '5px' }}>
                  {user.display_name || user.username}
                {activityInfo && (
                  <Label color={activityInfo.color} size='small' style={{ marginLeft: '10px', verticalAlign: 'middle' }}>
                    <Icon name={activityInfo.icon} />
                    {activityInfo.label}
                  </Label>
                )}
              </Header>
              <div style={{ color: 'rgba(255,255,255,0.8)' }}>
                <span style={{ marginRight: '20px' }}>
                  <Icon name='id badge' /> ID: {user.id}
                </span>
                <span style={{ marginRight: '20px' }}>
                  <Icon name='user' /> @{user.username}
                </span>
                {user.email && (
                  <span style={{ marginRight: '20px' }}>
                    <Icon name='mail' /> {user.email}
                  </span>
                )}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.7)', marginTop: '10px' }}>
                {enhancedSummary?.last_active && (
                  <span style={{ marginRight: '20px' }}>
                    <Icon name='clock' /> 最后活跃: {new Date(enhancedSummary.last_active).toLocaleString()}
                  </span>
                )}
                {user.created_at && (
                  <span>
                    <Icon name='calendar' /> 注册时间: {new Date(user.created_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </Grid.Column>
            <Grid.Column width={4} textAlign='right' verticalAlign='middle'>
              <Button.Group>
                <Button 
                  icon='arrow left'
                  content='返回'
                  onClick={() => navigate('/user')}
                  inverted
                />
                <Button 
                  icon='edit'
                  content='编辑'
                  onClick={() => navigate(`/user/${id}/edit`)}
                  inverted
                  color='blue'
                />
              </Button.Group>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        {/* 快速指标卡片 - 仅管理员可见 */}
        {isAdmin() && enhancedSummary && (
          <Grid stackable style={{ marginTop: '20px' }}>
            <Grid.Row columns={5}>
              <Grid.Column>
                <div style={{ 
                  background: 'rgba(255,255,255,0.15)', 
                  borderRadius: '8px', 
                  padding: '15px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
                    {enhancedSummary.today_requests || 0}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>今日请求</div>
                  {enhancedSummary.today_change !== undefined && (
                    <Label size='mini' color={enhancedSummary.today_change >= 0 ? 'green' : 'red'}>
                      {enhancedSummary.today_change >= 0 ? '↑' : '↓'} {Math.abs(enhancedSummary.today_change).toFixed(1)}%
                    </Label>
                  )}
                </div>
              </Grid.Column>
              <Grid.Column>
                <div style={{ 
                  background: 'rgba(255,255,255,0.15)', 
                  borderRadius: '8px', 
                  padding: '15px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
                    {enhancedSummary.week_requests || 0}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>周请求</div>
                </div>
              </Grid.Column>
              <Grid.Column>
                <div style={{ 
                  background: 'rgba(255,255,255,0.15)', 
                  borderRadius: '8px', 
                  padding: '15px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
                    {enhancedSummary.month_requests || 0}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>月请求</div>
                </div>
              </Grid.Column>
              <Grid.Column>
                <div style={{ 
                  background: 'rgba(255,255,255,0.15)', 
                  borderRadius: '8px', 
                  padding: '15px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
                    {(enhancedSummary.success_rate || 0).toFixed(1)}%
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>成功率</div>
                </div>
              </Grid.Column>
              <Grid.Column>
                <div style={{ 
                  background: 'rgba(255,255,255,0.15)', 
                  borderRadius: '8px', 
                  padding: '15px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
                    {enhancedSummary.avg_latency || 0}ms
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>平均延迟</div>
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        )}
      </Segment>

      <Tab 
        panes={panes} 
        menu={{ 
          secondary: true, 
          pointing: true,
          color: 'purple'
        }}
      />
    </Container>
  );
};

// 概览标签页
const OverviewTab = ({ user, enhancedSummary, activityTrend }) => {
  // 即使没有行为数据，也显示用户的基本配额信息
  const hasEnhancedData = enhancedSummary && (
    enhancedSummary.total_requests > 0 || 
    enhancedSummary.health_score !== undefined
  );

  const healthScore = enhancedSummary?.health_score || 0;
  const healthColor = healthScore >= 80 ? 'green' : healthScore >= 60 ? 'yellow' : healthScore >= 40 ? 'orange' : 'red';

  // 如果没有行为数据，显示用户基本配额信息
  if (!hasEnhancedData) {
    return (
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={16}>
            <Message info>
              <Message.Header>
                <Icon name='info circle' />
                用户行为数据
              </Message.Header>
              <p>该用户暂无行为日志记录，下面显示用户的基本配额信息。</p>
            </Message>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row columns={4}>
          <Grid.Column>
            <Card fluid color='blue'>
              <Card.Content>
                <Statistic size='small'>
                  <Statistic.Label>
                    <Icon name='dollar' />
                    总配额
                  </Statistic.Label>
                  <Statistic.Value style={{ fontSize: '1.5em' }}>
                    {renderQuota(user?.quota || 0)}
                  </Statistic.Value>
                </Statistic>
              </Card.Content>
            </Card>
          </Grid.Column>
          <Grid.Column>
            <Card fluid color='green'>
              <Card.Content>
                <Statistic size='small'>
                  <Statistic.Label>
                    <Icon name='chart line' />
                    已使用
                  </Statistic.Label>
                  <Statistic.Value style={{ fontSize: '1.5em' }}>
                    {renderQuota(user?.used_quota || 0)}
                  </Statistic.Value>
                </Statistic>
              </Card.Content>
            </Card>
          </Grid.Column>
          <Grid.Column>
            <Card fluid color='orange'>
              <Card.Content>
                <Statistic size='small'>
                  <Statistic.Label>
                    <Icon name='tasks' />
                    请求次数
                  </Statistic.Label>
                  <Statistic.Value style={{ fontSize: '1.5em' }}>
                    {renderNumber(user?.request_count || 0)}
                  </Statistic.Value>
                </Statistic>
              </Card.Content>
            </Card>
          </Grid.Column>
          <Grid.Column>
            <Card fluid color='purple'>
              <Card.Content>
                <Statistic size='small'>
                  <Statistic.Label>
                    <Icon name='calendar' />
                    注册时间
                  </Statistic.Label>
                  <Statistic.Value style={{ fontSize: '1.2em' }}>
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                  </Statistic.Value>
                </Statistic>
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }

  return (
    <Grid stackable>
      <Grid.Row>
        {/* 健康度评分 */}
        <Grid.Column width={4}>
          <Card fluid color={healthColor}>
            <Card.Content>
              <Card.Header>
                <Icon name='heartbeat' />
                用户健康度
              </Card.Header>
            </Card.Content>
            <Card.Content>
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ 
                  width: '120px', 
                  height: '120px', 
                  borderRadius: '50%', 
                  border: `8px solid ${healthColor === 'green' ? '#21ba45' : healthColor === 'yellow' ? '#fbbd08' : healthColor === 'orange' ? '#f2711c' : '#db2828'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto'
                }}>
                  <span style={{ fontSize: '36px', fontWeight: 'bold' }}>{healthScore}</span>
                </div>
                <p style={{ marginTop: '15px', color: '#666' }}>
                  {healthScore >= 80 ? '非常活跃' : healthScore >= 60 ? '较为活跃' : healthScore >= 40 ? '一般活跃' : '需要关注'}
                </p>
              </div>
              <Divider />
              <div style={{ fontSize: '12px', color: '#888' }}>
                <div style={{ marginBottom: '5px' }}>
                  <Icon name='fire' /> 活跃度: {enhancedSummary?.consecutive_days || 0} 天连续
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <Icon name='chart line' /> 使用量: {enhancedSummary?.total_requests || 0} 总请求
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <Icon name='check' /> 质量: {(enhancedSummary?.success_rate || 0).toFixed(1)}% 成功率
                </div>
              </div>
            </Card.Content>
          </Card>
        </Grid.Column>

        {/* 活跃度时间线 */}
        <Grid.Column width={8}>
          <Card fluid>
            <Card.Content>
              <Card.Header>
                <Icon name='calendar alternate' />
                活跃趋势（近30天）
              </Card.Header>
            </Card.Content>
            <Card.Content>
              {activityTrend && activityTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={activityTrend}>
                    <defs>
                      <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="requests" stroke="#8884d8" fillOpacity={1} fill="url(#colorRequests)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Message>暂无活动数据</Message>
              )}
            </Card.Content>
          </Card>
        </Grid.Column>

        {/* 快速信息 */}
        <Grid.Column width={4}>
          <Card fluid>
            <Card.Content>
              <Card.Header>
                <Icon name='info circle' />
                快速信息
              </Card.Header>
            </Card.Content>
            <Card.Content>
              <Table basic='very' compact>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell><Icon name='calendar plus' /> 首次活跃</Table.Cell>
                    <Table.Cell>
                      {enhancedSummary?.first_active ? 
                        new Date(enhancedSummary.first_active).toLocaleDateString() : 
                        '暂无'}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><Icon name='clock' /> 最后活跃</Table.Cell>
                    <Table.Cell>
                      {enhancedSummary?.last_active ? 
                        new Date(enhancedSummary.last_active).toLocaleString() : 
                        '暂无'}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><Icon name='fire' /> 连续活跃</Table.Cell>
                    <Table.Cell>
                      <Label size='small' color='blue'>
                        {enhancedSummary?.consecutive_days || 0} 天
                      </Label>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><Icon name='sun' /> 活跃时段</Table.Cell>
                    <Table.Cell>
                      <Label size='small' color='orange'>
                        {enhancedSummary?.peak_hour || 0}:00
                      </Label>
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Card.Content>
          </Card>

          {/* TOP 模型 */}
          {enhancedSummary?.top_models && enhancedSummary.top_models.length > 0 && (
            <Card fluid style={{ marginTop: '15px' }}>
              <Card.Content>
                <Card.Header>
                  <Icon name='star' />
                  常用模型
                </Card.Header>
              </Card.Content>
              <Card.Content>
                {enhancedSummary.top_models.map((model, index) => (
                  <div key={index} style={{ marginBottom: '8px' }}>
                    <Label size='small' color={COLORS[index % COLORS.length].replace('#', '')}>
                      {index + 1}
                    </Label>
                    <span style={{ marginLeft: '8px' }}>{model.model}</span>
                    <span style={{ float: 'right', color: '#888' }}>{model.count}</span>
                  </div>
                ))}
              </Card.Content>
            </Card>
          )}
        </Grid.Column>
      </Grid.Row>

      {/* 第二行：核心功能统计 */}
      <Grid.Row>
        {/* LLM 查询统计 */}
        <Grid.Column width={5}>
          <Card fluid color='blue'>
            <Card.Content>
              <Card.Header>
                <Icon name='comment alternate' />
                LLM 查询统计
              </Card.Header>
            </Card.Content>
            <Card.Content>
              <Statistic.Group size='mini' widths='three'>
                <Statistic color='blue'>
                  <Statistic.Value>{enhancedSummary?.llm_total_queries || 0}</Statistic.Value>
                  <Statistic.Label>总查询</Statistic.Label>
                </Statistic>
                <Statistic color='green'>
                  <Statistic.Value>{(enhancedSummary?.llm_success_rate || 0).toFixed(0)}%</Statistic.Value>
                  <Statistic.Label>成功率</Statistic.Label>
                </Statistic>
                <Statistic color='orange'>
                  <Statistic.Value>
                    {((enhancedSummary?.llm_total_tokens || 0) / 1000).toFixed(1)}K
                  </Statistic.Value>
                  <Statistic.Label>Tokens</Statistic.Label>
                </Statistic>
              </Statistic.Group>
              <Divider />
              <div style={{ fontSize: '12px', color: '#666' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span>今日查询:</span>
                  <span>{enhancedSummary?.llm_today_queries || 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span>本周查询:</span>
                  <span>{enhancedSummary?.llm_week_queries || 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>本月查询:</span>
                  <span>{enhancedSummary?.llm_month_queries || 0}</span>
                </div>
              </div>
            </Card.Content>
          </Card>
        </Grid.Column>

        {/* 执行记录统计 */}
        <Grid.Column width={5}>
          <Card fluid color='purple'>
            <Card.Content>
              <Card.Header>
                <Icon name='play' />
                执行记录统计
              </Card.Header>
            </Card.Content>
            <Card.Content>
              <Statistic.Group size='mini' widths='three'>
                <Statistic color='purple'>
                  <Statistic.Value>{enhancedSummary?.exec_total || 0}</Statistic.Value>
                  <Statistic.Label>总执行</Statistic.Label>
                </Statistic>
                <Statistic color='green'>
                  <Statistic.Value>{(enhancedSummary?.exec_success_rate || 0).toFixed(0)}%</Statistic.Value>
                  <Statistic.Label>成功率</Statistic.Label>
                </Statistic>
                <Statistic color='orange'>
                  <Statistic.Value>{enhancedSummary?.exec_avg_duration || 0}s</Statistic.Value>
                  <Statistic.Label>平均耗时</Statistic.Label>
                </Statistic>
              </Statistic.Group>
              <Divider />
              <div style={{ fontSize: '12px', color: '#666' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span>今日执行:</span>
                  <span>{enhancedSummary?.exec_today || 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span>成功执行:</span>
                  <span style={{ color: 'green' }}>{enhancedSummary?.exec_success || 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>失败执行:</span>
                  <span style={{ color: 'red' }}>{enhancedSummary?.exec_failed || 0}</span>
                </div>
              </div>
            </Card.Content>
          </Card>
        </Grid.Column>

        {/* 配额使用统计 */}
        <Grid.Column width={6}>
          <Card fluid color='teal'>
            <Card.Content>
              <Card.Header>
                <Icon name='money bill alternate' />
                配额使用
              </Card.Header>
            </Card.Content>
            <Card.Content>
              <Statistic.Group size='mini' widths='three'>
                <Statistic color='teal'>
                  <Statistic.Value style={{ fontSize: '1.5em' }}>
                    {renderQuota(enhancedSummary?.quota || 0)}
                  </Statistic.Value>
                  <Statistic.Label>总配额</Statistic.Label>
                </Statistic>
                <Statistic color='orange'>
                  <Statistic.Value style={{ fontSize: '1.5em' }}>
                    {renderQuota(enhancedSummary?.used_quota || 0)}
                  </Statistic.Value>
                  <Statistic.Label>已使用</Statistic.Label>
                </Statistic>
                <Statistic color='blue'>
                  <Statistic.Value style={{ fontSize: '1.5em' }}>
                    {renderNumber(enhancedSummary?.request_count || 0)}
                  </Statistic.Value>
                  <Statistic.Label>请求次数</Statistic.Label>
                </Statistic>
              </Statistic.Group>
              <Divider />
              <div style={{ marginTop: '10px' }}>
                <span style={{ fontSize: '12px', color: '#666' }}>配额使用率</span>
                <Progress 
                  percent={Math.min(enhancedSummary?.quota_usage_rate || 0, 100)} 
                  size='small' 
                  color={
                    (enhancedSummary?.quota_usage_rate || 0) > 90 ? 'red' : 
                    (enhancedSummary?.quota_usage_rate || 0) > 70 ? 'orange' : 'green'
                  }
                  progress 
                />
              </div>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '5px' }}>
                <Icon name='folder' /> 项目数量: {enhancedSummary?.project_count || 0}
              </div>
            </Card.Content>
          </Card>
        </Grid.Column>
      </Grid.Row>

      {/* 第三行：LLM 模型使用分布 */}
      {enhancedSummary?.llm_model_usage && enhancedSummary.llm_model_usage.length > 0 && (
        <Grid.Row>
          <Grid.Column width={16}>
            <Card fluid>
              <Card.Content>
                <Card.Header>
                  <Icon name='microchip' />
                  LLM 模型使用分布
                </Card.Header>
              </Card.Content>
              <Card.Content>
                <Table celled compact>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>模型</Table.HeaderCell>
                      <Table.HeaderCell>调用次数</Table.HeaderCell>
                      <Table.HeaderCell>Tokens消耗</Table.HeaderCell>
                      <Table.HeaderCell>占比</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {enhancedSummary.llm_model_usage.map((model, index) => {
                      const totalQueries = enhancedSummary.llm_total_queries || 1;
                      const percentage = ((model.count / totalQueries) * 100).toFixed(1);
                      return (
                        <Table.Row key={index}>
                          <Table.Cell>
                            <Label color={COLORS[index % COLORS.length].replace('#', '')} horizontal>
                              {model.model}
                            </Label>
                          </Table.Cell>
                          <Table.Cell>{model.count.toLocaleString()}</Table.Cell>
                          <Table.Cell>{(model.tokens / 1000).toFixed(1)}K</Table.Cell>
                          <Table.Cell>
                            <Progress percent={parseFloat(percentage)} size='small' color='blue'>
                              {percentage}%
                            </Progress>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table>
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
      )}
    </Grid>
  );
};

// 基本信息标签页
const BasicInfoTab = ({ user }) => (
  <Grid stackable>
    <Grid.Row>
      <Grid.Column width={8}>
        <Card fluid>
          <Card.Content>
            <Card.Header>
              <Icon name='user' />
              基本信息
            </Card.Header>
            <Card.Description>
              <Table basic='very' celled>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell><strong>用户名</strong></Table.Cell>
                    <Table.Cell>{user.username}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><strong>显示名称</strong></Table.Cell>
                    <Table.Cell>{user.display_name || '-'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><strong>邮箱</strong></Table.Cell>
                    <Table.Cell>{user.email || '-'}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><strong>分组</strong></Table.Cell>
                    <Table.Cell>
                      <Label color='teal'>{user.group || 'default'}</Label>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><strong>角色</strong></Table.Cell>
                    <Table.Cell>
                      {user.role === 100 ? <Label color='orange'>超级管理员</Label> :
                       user.role === 10 ? <Label color='yellow'>管理员</Label> :
                       <Label>普通用户</Label>}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><strong>状态</strong></Table.Cell>
                    <Table.Cell>
                      {user.status === 1 ? <Label color='green'>已激活</Label> :
                       user.status === 2 ? <Label color='red'>已封禁</Label> :
                       <Label color='grey'>未知</Label>}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><strong>创建时间</strong></Table.Cell>
                    <Table.Cell>{user.created_at ? new Date(user.created_at).toLocaleString('zh-CN') : '-'}</Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Card.Description>
          </Card.Content>
        </Card>
      </Grid.Column>
      
      <Grid.Column width={8}>
        <Card fluid>
          <Card.Content>
            <Card.Header>
              <Icon name='linkify' />
              账户绑定
            </Card.Header>
            <Card.Description>
              <Table basic='very' celled>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell><strong>GitHub</strong></Table.Cell>
                    <Table.Cell>
                      {user.github_id ? (
                        <Label color='black'>{user.github_id}</Label>
                      ) : (
                        <Label basic>未绑定</Label>
                      )}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><strong>微信</strong></Table.Cell>
                    <Table.Cell>
                      {user.wechat_id ? (
                        <Label color='green'>{user.wechat_id}</Label>
                      ) : (
                        <Label basic>未绑定</Label>
                      )}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><strong>邀请码</strong></Table.Cell>
                    <Table.Cell>
                      {user.invite_code_name ? (
                        <div>
                          <Label color='purple'>{user.invite_code_name}</Label>
                          <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                            代码: {user.invite_code_code}
                          </div>
                        </div>
                      ) : (
                        <Label basic>无</Label>
                      )}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell><strong>推荐人</strong></Table.Cell>
                    <Table.Cell>
                      {user.inviter_id ? (
                        <Label color='blue'>ID: {user.inviter_id}</Label>
                      ) : (
                        <Label basic>无</Label>
                      )}
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </Card.Description>
          </Card.Content>
        </Card>
      </Grid.Column>
    </Grid.Row>
  </Grid>
);

// 配额管理标签页
const QuotaManagementTab = ({ user, userStats }) => {
  const [repoQuotas, setRepoQuotas] = useState([]);
  const [loadingRepoQuotas, setLoadingRepoQuotas] = useState(true);
  const [editingCell, setEditingCell] = useState(null); // { quotaKey, field } - 记录正在编辑的单元格
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  
  const totalQuota = (user.quota || 0) + (user.used_quota || 0);
  const usagePercentage = totalQuota > 0 ? ((user.used_quota || 0) / totalQuota * 100) : 0;

  const fetchRepoQuotas = async () => {
    setLoadingRepoQuotas(true);
    try {
      const res = await API.get(`/api/admin/quotas/users/${user.id}`);
      const data = res.data?.data || res.data || [];
      setRepoQuotas(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('获取仓库配额失败:', err);
      setRepoQuotas([]);
    }
    setLoadingRepoQuotas(false);
  };

  useEffect(() => {
    if (user?.id) {
      fetchRepoQuotas();
    }
  }, [user?.id]);

  // 开始编辑
  const startEdit = (quotaKey, field, currentValue) => {
    setEditingCell({ quotaKey, field });
    setEditValue(String(currentValue || ''));
  };

  // 取消编辑
  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // 保存编辑
  const saveEdit = async (quotaKey) => {
    if (!editingCell || saving) return;

    const newValue = parseFloat(editValue);
    if (isNaN(newValue) || newValue < 0) {
      showError('请输入有效的数字');
      return;
    }

    setSaving(true);
    try {
      // 构建更新数据
      const updateData = repoQuotas.map(q => {
        if (q.quota_key === quotaKey) {
          return {
            quota_key: q.quota_key,
            limit: editingCell.field === 'limit' ? newValue : q.limit,
            usage: editingCell.field === 'usage' ? newValue : q.usage
          };
        }
        return {
          quota_key: q.quota_key,
          limit: q.limit,
          usage: q.usage
        };
      });

      const res = await API.put(`/api/admin/quotas/users/${user.id}`, { quotas: updateData });
      
      if (res.data.success) {
        // 刷新数据
        await fetchRepoQuotas();
        cancelEdit();
        // 可选：显示成功提示
        // showSuccess('更新成功');
      } else {
        showError(res.data.message || '更新失败');
      }
    } catch (err) {
      showError('更新配额失败: ' + (err.response?.data?.message || err.message));
    }
    setSaving(false);
  };

  // 处理键盘事件
  const handleKeyDown = (e, quotaKey) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit(quotaKey);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  return (
    <Grid stackable>
      {/* Token 配额部分 */}
      <Grid.Row>
        <Grid.Column width={16}>
          <Header as='h3'>
            <Icon name='dollar' />
            Token 配额
          </Header>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={8}>
          <Card fluid>
            <Card.Content>
              <Card.Header>
                <Icon name='dollar' />
                配额概览
              </Card.Header>
              <Card.Description>
                <Statistic.Group size='small' widths='three'>
                  <Statistic color='green'>
                    <Statistic.Value>{(user.quota || 0).toLocaleString()}</Statistic.Value>
                    <Statistic.Label>剩余配额</Statistic.Label>
                  </Statistic>
                  <Statistic color='orange'>
                    <Statistic.Value>{(user.used_quota || 0).toLocaleString()}</Statistic.Value>
                    <Statistic.Label>已用配额</Statistic.Label>
                  </Statistic>
                  <Statistic color='blue'>
                    <Statistic.Value>{totalQuota.toLocaleString()}</Statistic.Value>
                    <Statistic.Label>总配额</Statistic.Label>
                  </Statistic>
                </Statistic.Group>
                
                <Divider />
                
                <div>
                  <Label>配额使用率</Label>
                  <Progress 
                    percent={usagePercentage} 
                    color={usagePercentage > 80 ? 'red' : usagePercentage > 60 ? 'yellow' : 'green'}
                    indicating
                    label={`${usagePercentage.toFixed(1)}%`}
                  />
                </div>
              </Card.Description>
            </Card.Content>
          </Card>
        </Grid.Column>
        
        <Grid.Column width={8}>
          <Card fluid>
            <Card.Content>
              <Card.Header>
                <Icon name='chart bar' />
                使用统计
              </Card.Header>
              <Card.Description>
                {userStats ? (
                  <Statistic.Group size='small' widths='two'>
                    <Statistic color='blue'>
                      <Statistic.Value>{userStats.total_requests || 0}</Statistic.Value>
                      <Statistic.Label>总请求数</Statistic.Label>
                    </Statistic>
                    <Statistic color='green'>
                      <Statistic.Value>
                        {userStats.total_requests ? 
                          `${Math.round(((userStats.successful_requests || 0) / userStats.total_requests) * 100)}%` : 
                          '0%'
                        }
                      </Statistic.Value>
                      <Statistic.Label>成功率</Statistic.Label>
                    </Statistic>
                    <Statistic color='purple'>
                      <Statistic.Value>{(userStats.total_tokens || 0).toLocaleString()}</Statistic.Value>
                      <Statistic.Label>总Token数</Statistic.Label>
                    </Statistic>
                    <Statistic color='orange'>
                      <Statistic.Value>{userStats.total_cost || 0}</Statistic.Value>
                      <Statistic.Label>总成本</Statistic.Label>
                    </Statistic>
                  </Statistic.Group>
                ) : (
                  <Message>暂无使用统计数据</Message>
                )}
              </Card.Description>
            </Card.Content>
          </Card>
        </Grid.Column>
      </Grid.Row>

      <Divider section />

      {/* 系统配额部分 */}
      <Grid.Row>
        <Grid.Column width={16}>
          <Header as='h3'>
            <Icon name='code branch' />
            系统配额
          </Header>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={16}>
          <Card fluid>
            <Card.Content>
              <Card.Header>
                <Icon name='database' />
                详情
              </Card.Header>
            </Card.Content>
            <Card.Content>
              {loadingRepoQuotas ? (
                <Loader active inline='centered' content='加载中...' />
              ) : repoQuotas.length > 0 ? (
                <>
                  <div style={{ marginBottom: '10px', color: '#666', fontSize: '12px' }}>
                    <Icon name='info circle' />
                    提示：双击"额度"数值可直接编辑，按 Enter 保存，Esc 取消
                  </div>
                  <Table celled striped>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>配额项</Table.HeaderCell>
                        <Table.HeaderCell>名称</Table.HeaderCell>
                        <Table.HeaderCell>单位</Table.HeaderCell>
                        <Table.HeaderCell>
                          <Icon name='edit' size='small' />
                          额度
                        </Table.HeaderCell>
                        <Table.HeaderCell>已使用</Table.HeaderCell>
                        <Table.HeaderCell>剩余</Table.HeaderCell>
                        <Table.HeaderCell>使用率</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {repoQuotas.map((quota, index) => {
                        const displayLimit = typeof quota.limit === 'number' ? quota.limit : 0;
                        const displayUsage = typeof quota.usage === 'number' ? quota.usage : 0;
                        const displayRemaining = displayLimit - displayUsage;
                        
                        const usageRate = displayLimit > 0 ? (displayUsage / displayLimit * 100) : 0;

                        const isEditingLimit = editingCell?.quotaKey === quota.quota_key && editingCell?.field === 'limit';

                        return (
                          <Table.Row key={index}>
                            <Table.Cell>
                              <Label color='blue' horizontal>{quota.quota_key}</Label>
                            </Table.Cell>
                            <Table.Cell>{quota.name || '-'}</Table.Cell>
                            <Table.Cell>{quota.unit || '-'}</Table.Cell>
                            
                            {/* 额度 - 可编辑 */}
                            <Table.Cell 
                              style={{ 
                                cursor: isEditingLimit ? 'text' : 'pointer',
                                backgroundColor: isEditingLimit ? '#fff9e6' : 'transparent',
                                transition: 'background-color 0.2s'
                              }}
                              onDoubleClick={() => !isEditingLimit && startEdit(quota.quota_key, 'limit', displayLimit)}
                            >
                              {isEditingLimit ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(e, quota.quota_key)}
                                    onBlur={() => saveEdit(quota.quota_key)}
                                    autoFocus
                                    disabled={saving}
                                    style={{
                                      width: '120px',
                                      padding: '5px 8px',
                                      border: '2px solid #2185d0',
                                      borderRadius: '4px',
                                      fontSize: '14px'
                                    }}
                                  />
                                  <Icon 
                                    name='check' 
                                    color='green' 
                                    link={!saving}
                                    onClick={() => !saving && saveEdit(quota.quota_key)}
                                    style={{ cursor: saving ? 'not-allowed' : 'pointer' }}
                                  />
                                  <Icon 
                                    name='close' 
                                    color='red' 
                                    link={!saving}
                                    onClick={() => !saving && cancelEdit()}
                                    style={{ cursor: saving ? 'not-allowed' : 'pointer' }}
                                  />
                                  {saving && <Icon name='spinner' loading />}
                                </div>
                              ) : (
                                <strong 
                                  style={{ 
                                    padding: '4px 8px',
                                    borderRadius: '3px',
                                    display: 'inline-block'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                  title="双击编辑"
                                >
                                  {displayLimit.toFixed(2)}
                                  <Icon name='edit outline' size='small' style={{ marginLeft: '5px', opacity: 0.5 }} />
                                </strong>
                              )}
                            </Table.Cell>

                            {/* 已使用 - 只读 */}
                            <Table.Cell>
                              <Label color='orange'>
                                {displayUsage.toFixed(2)}
                              </Label>
                            </Table.Cell>

                            {/* 剩余 - 只读 */}
                            <Table.Cell>
                              <Label color={displayRemaining > 0 ? 'green' : 'red'}>
                                {displayRemaining.toFixed(2)}
                              </Label>
                            </Table.Cell>

                            {/* 使用率 - 只读 */}
                            <Table.Cell>
                              <div style={{ padding: '4px 0' }}>
                                <Progress
                                  percent={usageRate}
                                  size="small"
                                  color={
                                    usageRate >= 85
                                      ? 'red'
                                      : usageRate >= 70
                                      ? 'orange'
                                      : 'green'
                                  }
                                  progress
                                />
                              </div>
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table>
                </>
              ) : (
                <Message info>
                  <Message.Header>暂无仓库配额数据</Message.Header>
                  <p>该用户暂未配置仓库配额限制。</p>
                </Message>
              )}
            </Card.Content>
          </Card>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

// 行为分析标签页（管理员专用）
const BehaviorAnalysisTab = ({ summary, trend, hourly, models, logs }) => {
  return (
    <div>
      {/* 行为概览 */}
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={4}>
            <Statistic size='small' color='blue'>
              <Statistic.Value>{summary.today_requests || 0}</Statistic.Value>
              <Statistic.Label>今日请求</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column width={4}>
            <Statistic size='small' color='green'>
              <Statistic.Value>{summary.week_requests || 0}</Statistic.Value>
              <Statistic.Label>本周请求</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column width={4}>
            <Statistic size='small' color='orange'>
              <Statistic.Value>{summary.month_requests || 0}</Statistic.Value>
              <Statistic.Label>本月请求</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column width={4}>
            <Statistic size='small' color='purple'>
              <Statistic.Value>{summary.consecutive_days || 0}</Statistic.Value>
              <Statistic.Label>连续活跃天数</Statistic.Label>
            </Statistic>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <Divider />

      {/* 图表分析 */}
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={8}>
            <Card fluid>
              <Card.Content>
              <Card.Header>
                <Icon name='line graph' />
                活动趋势
              </Card.Header>
              </Card.Content>
              <Card.Content>
                {trend && trend.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="requests" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                  </AreaChart>
                </ResponsiveContainer>
                ) : (
                  <Message>暂无趋势数据</Message>
                )}
              </Card.Content>
            </Card>
          </Grid.Column>
          
          <Grid.Column width={8}>
            <Card fluid>
              <Card.Content>
              <Card.Header>
                <Icon name='clock' />
                活动时间分布
              </Card.Header>
              </Card.Content>
              <Card.Content>
                {hourly && hourly.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={hourly}>
                    <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="requests" fill="#82ca9d" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                ) : (
                  <Message>暂无小时分布数据</Message>
                )}
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
        
        <Grid.Row>
          <Grid.Column width={8}>
            <Card fluid>
              <Card.Content>
              <Card.Header>
                <Icon name='settings' />
                模型使用偏好
              </Card.Header>
              </Card.Content>
              <Card.Content>
                {models && models.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={models}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {models.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                ) : (
                  <Message>暂无模型使用数据</Message>
                )}
              </Card.Content>
            </Card>
          </Grid.Column>
          
          <Grid.Column width={8}>
            <Card fluid>
              <Card.Content>
              <Card.Header>
                <Icon name='history' />
                最近活动日志
              </Card.Header>
              </Card.Content>
              <Card.Content>
                {logs && logs.length > 0 ? (
                  <Table compact size='small'>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>时间</Table.HeaderCell>
                      <Table.HeaderCell>操作</Table.HeaderCell>
                      <Table.HeaderCell>状态</Table.HeaderCell>
                      <Table.HeaderCell>耗时</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {logs.slice(0, 5).map((log, index) => (
                      <Table.Row key={index}>
                        <Table.Cell>{new Date(log.timestamp).toLocaleTimeString()}</Table.Cell>
                          <Table.Cell>
                            <Popup
                              content={log.action}
                              trigger={<span style={{ cursor: 'help' }}>{log.action?.substring(0, 30)}...</span>}
                            />
                          </Table.Cell>
                        <Table.Cell>
                          <Label color={log.status >= 200 && log.status < 300 ? 'green' : 'red'} size='mini'>
                            {log.status}
                          </Label>
                        </Table.Cell>
                        <Table.Cell>{log.duration}ms</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
                ) : (
                  <Message>暂无日志数据</Message>
                )}
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

// API使用标签页（管理员专用）
const APIUsageTab = ({ apiStats }) => {
  if (!apiStats || apiStats.length === 0) {
    return (
      <Message info>
        <Message.Header>暂无 API 使用数据</Message.Header>
        <p>该用户近期没有 API 调用记录。</p>
      </Message>
    );
  }

  return (
    <div>
      <Card fluid>
        <Card.Content>
          <Card.Header>
            <Icon name='code' />
            API 端点使用统计（近30天）
          </Card.Header>
        </Card.Content>
        <Card.Content>
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>API 端点</Table.HeaderCell>
                <Table.HeaderCell>方法</Table.HeaderCell>
                <Table.HeaderCell>调用次数</Table.HeaderCell>
                <Table.HeaderCell>成功率</Table.HeaderCell>
                <Table.HeaderCell>平均延迟</Table.HeaderCell>
                <Table.HeaderCell>P95延迟</Table.HeaderCell>
                <Table.HeaderCell>最后使用</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {apiStats.map((stat, index) => (
                <Table.Row key={index}>
                  <Table.Cell>
                    <Popup
                      content={stat.endpoint}
                      trigger={
                        <span style={{ cursor: 'help' }}>
                          {stat.endpoint?.length > 40 ? stat.endpoint.substring(0, 40) + '...' : stat.endpoint}
                        </span>
                      }
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Label size='mini' color={stat.method === 'POST' ? 'green' : stat.method === 'GET' ? 'blue' : 'grey'}>
                      {stat.method}
                    </Label>
                  </Table.Cell>
                  <Table.Cell>
                    <strong>{stat.total_requests?.toLocaleString()}</strong>
                  </Table.Cell>
                  <Table.Cell>
                    <Label 
                      color={stat.success_rate >= 99 ? 'green' : stat.success_rate >= 95 ? 'yellow' : 'red'}
                      size='small'
                    >
                      {stat.success_rate?.toFixed(1)}%
                    </Label>
                  </Table.Cell>
                  <Table.Cell>
                    <Label 
                      color={stat.avg_latency < 1000 ? 'green' : stat.avg_latency < 3000 ? 'yellow' : 'red'}
                      size='small'
                    >
                      {stat.avg_latency}ms
                    </Label>
                  </Table.Cell>
                  <Table.Cell>
                    {stat.p95_latency ? `${stat.p95_latency}ms` : '-'}
                  </Table.Cell>
                  <Table.Cell>
                    {stat.last_used ? new Date(stat.last_used).toLocaleString() : '-'}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Card.Content>
      </Card>
    </div>
  );
};

// 数据统计 Tab（仓库、执行、设备）
const DataStatsTab = ({ dataStats, navigate, userId }) => {
  const [activeSection, setActiveSection] = useState('overview');
  
  if (!dataStats) return null;

  const successRate = dataStats.execution_count > 0 
    ? ((dataStats.success_count / dataStats.execution_count) * 100).toFixed(1)
    : 0;

  const menuItems = [
    { key: 'overview', icon: 'dashboard', label: '概览' },
    ...(gitEnabled ? [{ key: 'repos', icon: 'git', label: `仓库 (${dataStats.repo_count || 0})` }] : []),
    { key: 'executions', icon: 'code', label: `执行 (${dataStats.execution_count || 0})` },
    { key: 'devices', icon: 'computer', label: `设备 (${dataStats.device_count || 0})` },
  ];

  return (
    <div>
      {/* 子菜单 */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {menuItems.map(item => (
          <Button 
            key={item.key}
            basic={activeSection !== item.key}
            primary={activeSection === item.key}
            onClick={() => setActiveSection(item.key)}
          >
            <Icon name={item.icon} /> {item.label}
          </Button>
        ))}
      </div>

      {/* 概览视图 */}
      {activeSection === 'overview' && (
        <Grid columns={gitEnabled ? 4 : 3} stackable>
          {gitEnabled && (
            <Grid.Column>
              <Segment 
                textAlign="center" 
                style={{ cursor: 'pointer' }}
                onClick={() => setActiveSection('repos')}
              >
                <Statistic size="small">
                  <Statistic.Value>{dataStats.repo_count || 0}</Statistic.Value>
                  <Statistic.Label><Icon name="git" /> 仓库数量</Statistic.Label>
                </Statistic>
              </Segment>
            </Grid.Column>
          )}
          <Grid.Column>
            <Segment 
              textAlign="center"
              style={{ cursor: 'pointer' }}
              onClick={() => setActiveSection('executions')}
            >
              <Statistic size="small">
                <Statistic.Value>{dataStats.execution_count || 0}</Statistic.Value>
                <Statistic.Label><Icon name="code" /> 执行次数</Statistic.Label>
              </Statistic>
            </Segment>
          </Grid.Column>
          <Grid.Column>
            <Segment textAlign="center">
              <Statistic size="small" color="green">
                <Statistic.Value>{successRate}%</Statistic.Value>
                <Statistic.Label><Icon name="check" /> 成功率</Statistic.Label>
              </Statistic>
            </Segment>
          </Grid.Column>
          <Grid.Column>
            <Segment 
              textAlign="center"
              style={{ cursor: 'pointer' }}
              onClick={() => setActiveSection('devices')}
            >
              <Statistic size="small">
                <Statistic.Value>{dataStats.device_count || 0}</Statistic.Value>
                <Statistic.Label><Icon name="computer" /> 设备数量</Statistic.Label>
              </Statistic>
            </Segment>
          </Grid.Column>
        </Grid>
      )}

      {/* 仓库列表视图 */}
      {gitEnabled && activeSection === 'repos' && (
        <Card fluid>
          <Card.Content>
            <Card.Header>
              <Icon name="git" /> 用户仓库
              <Label circular color="blue" style={{ marginLeft: 10 }}>{dataStats.repo_count || 0}</Label>
            </Card.Header>
          </Card.Content>
          <Card.Content>
            {dataStats.repos && dataStats.repos.length > 0 ? (
              <Table basic="very" compact selectable>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>#</Table.HeaderCell>
                    <Table.HeaderCell>仓库名称</Table.HeaderCell>
                    <Table.HeaderCell>操作</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {dataStats.repos.map((repo, idx) => (
                    <Table.Row key={idx}>
                      <Table.Cell>{idx + 1}</Table.Cell>
                      <Table.Cell>
                        <Icon name="folder" color="blue" />
                        <strong>{repo}</strong>
                      </Table.Cell>
                      <Table.Cell>
                        <Button size="mini" basic onClick={() => {
                          // 可以跳转到仓库详情或执行记录筛选
                          setActiveSection('executions');
                        }}>
                          <Icon name="search" /> 查看执行
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            ) : (
              <Message info>该用户暂无仓库记录</Message>
            )}
          </Card.Content>
        </Card>
      )}

      {/* 执行记录视图 */}
      {activeSection === 'executions' && (
        <Card fluid>
          <Card.Content>
            <Card.Header>
              <Icon name="code" /> 执行记录
              <Label circular color="blue" style={{ marginLeft: 10 }}>{dataStats.execution_count || 0}</Label>
              <Button 
                size="mini" 
                floated="right" 
                onClick={() => navigate('/data-collection')}
              >
                <Icon name="external" /> 在执行管理中查看全部
              </Button>
            </Card.Header>
          </Card.Content>
          <Card.Content style={{ maxHeight: 500, overflowY: 'auto' }}>
            {dataStats.recent_executions && dataStats.recent_executions.length > 0 ? (
              <Table basic="very" compact selectable>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>执行ID</Table.HeaderCell>
                    <Table.HeaderCell>仓库</Table.HeaderCell>
                    <Table.HeaderCell>文件路径</Table.HeaderCell>
                    <Table.HeaderCell>状态</Table.HeaderCell>
                    <Table.HeaderCell>耗时</Table.HeaderCell>
                    <Table.HeaderCell>时间</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {dataStats.recent_executions.map((exec, idx) => (
                    <Table.Row key={idx}>
                      <Table.Cell>
                        <code style={{ fontSize: 11 }}>{exec.id?.substring(0, 16)}...</code>
                      </Table.Cell>
                      <Table.Cell>
                        <Icon name="folder" color="blue" />
                        {exec.repo_name}
                      </Table.Cell>
                      <Table.Cell>
                        <code style={{ fontSize: 11 }}>{exec.file_path?.substring(0, 30) || '-'}</code>
                      </Table.Cell>
                      <Table.Cell>
                        <Label 
                          size="tiny" 
                          color={exec.status === 'completed' ? 'green' : exec.status === 'failed' ? 'red' : exec.status === 'running' ? 'blue' : 'grey'}
                        >
                          {exec.status === 'completed' ? '成功' : exec.status === 'failed' ? '失败' : exec.status === 'running' ? '运行中' : exec.status}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>
                        {exec.duration_ms ? `${exec.duration_ms}ms` : '-'}
                      </Table.Cell>
                      <Table.Cell style={{ fontSize: 11 }}>
                        {exec.created_at ? new Date(exec.created_at).toLocaleString() : '-'}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            ) : (
              <Message info>该用户暂无执行记录</Message>
            )}
            {dataStats.execution_count > 10 && (
              <Message size="small">
                <Icon name="info circle" />
                仅显示最近 10 条记录，
                <span 
                  onClick={() => navigate('/data-collection')}
                  style={{ cursor: 'pointer', color: '#2185D0', textDecoration: 'underline' }}
                >
                  点击查看全部
                </span>
              </Message>
            )}
          </Card.Content>
        </Card>
      )}

      {/* 设备列表视图 */}
      {activeSection === 'devices' && (
        <Card fluid>
          <Card.Content>
            <Card.Header>
              <Icon name="computer" /> 用户设备
              <Label circular color="blue" style={{ marginLeft: 10 }}>{dataStats.device_count || 0}</Label>
              <Button 
                size="mini" 
                floated="right" 
                onClick={() => navigate('/data-collection')}
              >
                <Icon name="external" /> 在设备管理中查看
              </Button>
            </Card.Header>
          </Card.Content>
          <Card.Content>
            {dataStats.devices && dataStats.devices.length > 0 ? (
              <Table basic="very" compact selectable>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>设备名称</Table.HeaderCell>
                    <Table.HeaderCell>操作系统</Table.HeaderCell>
                    <Table.HeaderCell>设备类型</Table.HeaderCell>
                    <Table.HeaderCell>状态</Table.HeaderCell>
                    <Table.HeaderCell>最后活跃</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {dataStats.devices.map((device, idx) => (
                    <Table.Row key={idx}>
                      <Table.Cell>
                        <Icon name="computer" color="grey" />
                        <strong>{device.name || device.device_name || `设备${idx + 1}`}</strong>
                      </Table.Cell>
                      <Table.Cell>{device.os_type || device.os || '-'}</Table.Cell>
                      <Table.Cell>{device.device_type || '-'}</Table.Cell>
                      <Table.Cell>
                        <Label 
                          size="tiny" 
                          color={device.status === 1 ? 'green' : 'grey'}
                        >
                          {device.status === 1 ? '正常' : '禁用'}
                        </Label>
                      </Table.Cell>
                      <Table.Cell style={{ fontSize: 11 }}>
                        {device.last_seen_at ? new Date(device.last_seen_at).toLocaleString() : '-'}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            ) : (
              <Message info>该用户暂无设备记录</Message>
            )}
          </Card.Content>
        </Card>
      )}
    </div>
  );
};

export default UserInfo;
